import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickOutside, useSetDropdownWidth, calculateDropdownPosition } from 'services/utils/dropdownBehavior'
import { FieldOption } from 'types/FieldTypes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import '../../../style/scss/forms.scss';

interface CustomDropdownProps {
    name?: string;
    id?: string;
    options: FieldOption[];
    value: string | number | null;
    selectedOption?: FieldOption | null;
    onChange: (selectedValueOrId: string) => void;
    disabled_selected?: string;
    disabled?: boolean;
    showSearch?: boolean;
    errorMessage?: string | string[] | undefined;
    selectionFormat?: string;
    optionFormat?: string;
    allowNoneOption?: boolean;
    backendField?: string;
    orderOptions?: boolean;
    groupByField?: string; // Name of the field in the options to group them on
    formatGroupTitle?: (group: string) => string; // Formats the group title with the given function
    restrictToGroupValue?: string; // When given, only shows the options of this group value, as a flat list without the group title
    openOnMount?: boolean; // Opens the option list as soon as the dropdown renders, used when entering the edit mode of a live edit field
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
    name, id, options, onChange, disabled_selected, selectionFormat = 'name', optionFormat = 'name',
    selectedOption, allowNoneOption, errorMessage, showSearch = true, disabled, backendField, orderOptions,
    groupByField, formatGroupTitle, restrictToGroupValue, openOnMount
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const dropdownRef = useRef<HTMLDivElement>(null);
    const dropdownListRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const fieldRef = useRef<HTMLDivElement>(null);
    const [dropdownWidth, setDropdownWidth] = useState<number>(200);
    const [dropdownAbove, setDropdownAbove] = useState(false);
    const [dropdownMaxHeight, setDropdownMaxHeight] = useState<number | undefined>(undefined);

    // Use dropdownBehavior.ts to close the dropdown when clicking outside
    useClickOutside(isOpen, dropdownRef, setIsOpen);

    // Use dropdownBehavior.ts to set the dropdown width on open
    useSetDropdownWidth(fieldRef, setDropdownWidth);

    // Autofocus the search field when opening the dropdown list
    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => {
                searchRef.current?.focus();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    // Open the option list right away when asked, after the field has its position
    useEffect(() => {
        if (openOnMount && !disabled) {
            calculateDropdownPosition(fieldRef, setDropdownAbove, setDropdownMaxHeight);
            setIsOpen(true);
        }
        // eslint-disable-next-line
    }, []);

    // Handle the opening or closing of the dropdown
    const handleToggleDropdown = (e: React.MouseEvent<HTMLDivElement>) => {
        if (disabled) return;
        e.stopPropagation();

        // Calculate the dropdown position
        if (!isOpen) {
            calculateDropdownPosition(fieldRef, setDropdownAbove, setDropdownMaxHeight);
        }

        // Open or close the dropdown
        setIsOpen(prev => !prev);
    };

    // Filter the options on the search value, and sort them if order options prop is true
    const filteredOptions = options
        .filter(option => {
            // If the label of the given or fetched dropdown options are empty, filter them out so they are not mapped in the dropdown
            if (!optionFormat || (optionFormat && option[optionFormat] && option[optionFormat]?.trim() !== "")) {
                // Checks if one of the values of the option contains the search value
                return Object.values(option).some(value => String(value).toLowerCase().includes(search.toLowerCase()));
            }
            return false;
        })
        .sort((a, b) => orderOptions ? a.ordering - b.ordering : 0);

    // Restrict the options to a single group value, if given, shown as a flat list
    const effectiveOptions = groupByField && restrictToGroupValue
        ? filteredOptions.filter(option => String(option[groupByField]) === restrictToGroupValue)
        : filteredOptions;

    // Group the effective options by field, if given
    const groupedOptions = groupByField && !restrictToGroupValue
        ? effectiveOptions.reduce((result, option) => {
            const group = option[groupByField] ? String(option[groupByField]) : '';
            if (group) {
                if (result[group]) {
                    result[group].push(option);
                } else {
                    result[group] = [option];
                }
            }
            return result;
        }, {} as Record<string, FieldOption[]>)
        : {};
    const groupNames = Object.keys(groupedOptions);

    // Renders a single dropdown option
    const renderOption = (option: FieldOption) => {
        // Determines the active value
        const isActive = selectedOption
            ? (option.id && selectedOption.id === option.id) || (option.value && selectedOption.value === option.value)
            : false;

        return (
            <div key={String(option.id || option.value)}
                 className={`custom-dropdown-item ${isActive ? 'active' : ''}`}
                 onClick={() => { onChange(String(backendField ? option[backendField] : (option.id || option.value))); setIsOpen(false); }}>
                    {t(option[optionFormat]) || option[optionFormat]}
                    {isActive && (
                        <FontAwesomeIcon
                            icon={faCheck}
                            className='custom-dropdown-selected-icon' />
                    )}
            </div>
        )
    };

    return (
        <div className='custom-dropdown-container dropdown-container'>
            <div className={`custom-dropdown-field ${errorMessage ? 'is-invalid' : ''} ${disabled ? 'disabled' : ''}`}
                 onMouseDown={handleToggleDropdown}
                 ref={fieldRef}>
                    {selectedOption 
                        ? t(selectedOption[selectionFormat] || selectedOption[selectionFormat])
                        : <span className='custom-dropdown-placeholder'>{t(disabled_selected) || t('forms.general_disabled_selected')}</span>}
                    <FontAwesomeIcon 
                        icon={isOpen && !disabled ? faCaretUp : faCaretDown}
                        className='custom-dropdown-caret' />
            </div>
            {isOpen && !disabled && (
                <div ref={dropdownRef}
                     className={`custom-dropdown ${dropdownAbove ? 'dropdown-above' : ''}`}  
                     style={{width: dropdownWidth, maxHeight: dropdownMaxHeight}}>
                    <div ref={dropdownListRef}
                         className='custom-dropdown-list' 
                         style={{width: dropdownWidth}}>
                        {showSearch && (
                            <input 
                                type="text" 
                                id={id}
                                name={name}
                                value={search} 
                                className='custom-dropdown-search'
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={t('forms.search_placeholder')} 
                                ref={searchRef}
                            />
                        )}
                        {allowNoneOption !== false &&
                            <div className="custom-dropdown-item" onClick={() => { onChange('none'); setIsOpen(false); }}>
                                ({t('forms.none_option')})
                            </div>
                        }
                        {groupByField && !restrictToGroupValue ? (
                            groupNames.map((group, index) => (
                                <div key={group}>
                                    <div className={index === 0 ? 'custom-dropdown-group-title first' : 'custom-dropdown-group-title'}>
                                        {formatGroupTitle ? formatGroupTitle(group) : group}
                                    </div>
                                    {groupedOptions[group].map(option => renderOption(option))}
                                </div>
                            ))
                        ) : (
                            effectiveOptions.map(option => renderOption(option))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomDropdown;